/**
 * The Gravity Gloves (a melee weapon): hold attack and a blue beam shoots out of your hands. It grabs the first
 * box, crate or barrel it touches (or a gun lying on the ground, which it pulls to you) and holds it at the end of
 * a springy line. The mouse wheel pushes the line out or pulls it in; swing and let go to throw, or right-click to
 * launch it straight ahead. Held right against another box or barrel, V merges the two (they stay stuck together
 * in blue liquid and move as one). Players are never moved by the beam, only hit by what you throw. Right-click with
 * nothing held fires a short plasma burst (owner request, 16 October 2026: the gloves were "useless when it comes to
 * hitting/shooting").
 *
 * Shared by the server (beam, physics, impacts) and the browser (beam, liquid and prompt drawing).
 * Distances are game units (a player is 2.25 across the middle), speeds are units per millisecond (you run at
 * 0.03), times are milliseconds.
 */
export const GravityGloves = Object.freeze({
    /** the gloves' item id */
    idString: "gravity_gloves",
    /** how far the beam reaches to grab something (from the hands) */
    reach: 30,
    /** how fast the beam shoots out while it looks for something to grab */
    beamSpeed: 0.45,
    /** the beam leaves the hands this far in front of your centre */
    handsOffset: 2.6,
    /** objects within this distance of the beam's line count as touched (the beam is easier to aim than a bullet) */
    aimSlack: 1.2,
    /** the line: how far from your centre it holds things (the closest also keeps big things clear of you) */
    minLength: 6,
    maxLength: 26,
    /** one notch of the mouse wheel */
    lengthStep: 2.5,
    /** the line's spring: how stiff (radians per second, for something of mass 1; heavier things lag more) */
    stiffness: 15,
    /** and how quickly it stops wobbling (1: no overshoot at all; lower: more elastic) */
    damping: 0.55,
    /** the fastest anything moves (mass 1); heavier things are slower: maxSpeed / mass^0.2 */
    maxSpeed: 0.16,
    /** sliding to a stop once let go: drag (a share of the speed per ms) and a little steady friction */
    drag: 0.002,
    friction: 0.000015,
    /** ...more on ice, less in water */
    iceDragFactor: 0.35,
    waterDragFactor: 2.5,
    /** slower than this, a free object stops and stays put again */
    restSpeed: 0.004,
    /** the line snaps if the held thing is stuck this far from where it should be, for this long */
    snapDistance: 24,
    snapMs: 500,
    /** ...or if a wall comes between you and it for this long */
    blockedMs: 300,
    /** merging: the held thing must be this close to another box or barrel, and a group holds this many at most */
    mergeGap: 1.6,
    maxGroup: 4,
    /** a box or barrel bumped slower than this just stops the one bumping it (faster, it's knocked away) */
    knockSpeed: 0.03,
    /** guns: pulled toward you at this speed, and picked up (into an empty slot) or dropped at your feet when this close */
    pullSpeed: 0.075,
    pickupDistance: 3.4,
    /** impacts: below this speed nothing is hurt; above it, damage = (speed − min) × factor × mass^0.6, at most max */
    impactMinSpeed: 0.035,
    impactFactor: 230,
    impactMaxDamage: 55,
    /** what something thrown does to walls, trees, doors and windows it hits (a share of the impact) */
    obstacleImpactShare: 0.5,
    /** something thrown hits the same player at most this often */
    impactCooldown: 400,
    /** your own throw can't hurt you for this long after you let go (it can bounce back later) */
    throwerGraceMs: 350,
    /**
     * Right-click with nothing held: a plasma burst out of the hands (the bolt is a bullet: `gravity_gloves_bullet` in
     * definitions/bullets.ts). Its damage (before armour), how far it flies, how fast, how often one can be fired, and a
     * little spread either way (radians)
     */
    plasmaDamage: 22,
    plasmaRange: 30,
    plasmaSpeed: 0.22,
    plasmaCooldown: 380,
    plasmaSpread: 0.025
});

/** the plasma burst's bullet (definitions/bullets.ts): what the server fires and the browser draws */
export const GRAVITY_PLASMA_BULLET = "gravity_gloves_bullet";

/** what a beam is doing (sent with every player who has one) */
export const enum GravityBeamMode {
    Off,
    /** shooting out, looking for something to grab */
    Reaching,
    /** holding a box, crate or barrel (or a merged group) */
    Holding,
    /** pulling a gun toward its owner */
    Pulling
}

export interface GravityBeamData {
    readonly mode: GravityBeamMode
    /** reaching: how far the beam has shot out; holding: how far from you the line holds things */
    readonly length: number
    /** what's held or pulled (holding, pulling): the object's id */
    readonly target?: number
    /** holding: the box or barrel that V would merge the held thing with */
    readonly merge?: number
}

/**
 * Everything the gloves can pick up, with how heavy it is (a box weighs 1), how bouncy, and how easily it breaks
 * when it hits something (a share of the impact's damage it takes itself). Anything else (walls, trees, rocks,
 * cabinets, big tanks, sandbags, and the ammo crate, which only a sharp blade opens) stays put.
 */
export interface GravityObjectStats {
    readonly mass: number
    readonly restitution: number
    readonly fragility: number
}

const crate = (mass: number, fragility = 1.2): GravityObjectStats => ({ mass, restitution: 0.2, fragility });
const barrel = (mass: number, fragility = 0.9): GravityObjectStats => ({ mass, restitution: 0.35, fragility });

export const GravityObjects: Readonly<Record<string, GravityObjectStats>> = Object.freeze({
    box: { mass: 1, restitution: 0.25, fragility: 1.6 },
    grenade_box: { mass: 1, restitution: 0.25, fragility: 1.6 },
    regular_crate: crate(3),
    flint_crate: crate(3),
    aegis_crate: crate(3),
    lansirama_crate: crate(3),
    nsd_crate: crate(3),
    campsite_crate: crate(2.6),
    grenade_crate: crate(2.2),
    lighthouse_crate: crate(2.2),
    tear_gas_crate: crate(2.8),
    barrel: barrel(4),
    super_barrel: barrel(4.5, 0.8),
    wine_barrel: barrel(4, 1),
    special_wine_barrel: barrel(4, 1),
    propane_tank: { mass: 1.5, restitution: 0.45, fragility: 1.1 }
});

export const isGravityObject = (idString: string): boolean => Object.hasOwn(GravityObjects, idString);

/** the fastest something of this mass can move */
export const gravityMaxSpeed = (mass: number): number => GravityGloves.maxSpeed / Math.pow(Math.max(1, mass), 0.2);

/** the damage something of this mass does when it hits at this speed (before armour) */
export const gravityImpactDamage = (speed: number, mass: number): number => {
    const { impactMinSpeed, impactFactor, impactMaxDamage } = GravityGloves;
    if (speed <= impactMinSpeed) return 0;
    return Math.min(impactMaxDamage, (speed - impactMinSpeed) * impactFactor * Math.pow(Math.max(0.5, mass), 0.6));
};
